import { Fragment, useEffect, useMemo, useRef, useState } from "react";

const BLOG_BASE_URL = process.env.REACT_APP_HTML_FEATURE_BLOG_URL;

const getBlogSource = (details) => {
  if (!details) return null;
  if (details.isFamily == true && !details.sku) return { type: "family", id: details.familyId };
  return { type: "product", id: details.sku };
};

const IFRAME_SANDBOX =
  "allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation";

const readSiteStyles = () => {
  const bootstrapLink = document.querySelector('link[rel="stylesheet"][href*="bootstrap.min.css"]');
  const bodyStyle = getComputedStyle(document.body);
  return {
    bootstrapHref: bootstrapLink ? bootstrapLink.href : null,
    fontFamily: bodyStyle.fontFamily,
    color: bodyStyle.color,
  };
};

const splitBlogHtml = (html) => {
  const source = html ?? "";
  if (!/<(html|head|body)[\s>]/i.test(source)) return { head: "", body: source };
  const parsed = new DOMParser().parseFromString(source, "text/html");
  parsed.querySelectorAll("title, meta").forEach((node) => node.remove());
  return { head: parsed.head.innerHTML, body: parsed.body.innerHTML };
};

const buildBlogDocument = (html) => {
  const { bootstrapHref, fontFamily, color } = readSiteStyles();
  const bootstrap = bootstrapHref ? `<link rel="stylesheet" href="${bootstrapHref}">` : "";
  const { head, body } = splitBlogHtml(html);
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><base target="_top">${bootstrap}${head}<style>html,body{margin:0;background:transparent;overflow:hidden;font-family:${fontFamily};color:${color};height:auto !important;min-height:0 !important;}</style></head><body>${body}</body></html>`;
};

const scrollToBlogTarget = (frame, target, isInFrame) => {
  const targetTop = target.getBoundingClientRect().top + (isInFrame ? frame.getBoundingClientRect().top : 0);
  const overlay = frame.closest(".product-overlay");
  if (overlay) {
    const bar = overlay.querySelector(".product-overlay-bar");
    const visibleTop = overlay.getBoundingClientRect().top + (bar ? bar.offsetHeight : 0);
    overlay.scrollBy({ top: targetTop - visibleTop, behavior: "smooth" });
    return;
  }
  const header = document.querySelector("header.header-area");
  const visibleTop = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
  window.scrollBy({ top: targetTop - visibleTop, behavior: "smooth" });
};

const handleHashLinkClick = (frame, event) => {
  const link = event.target.closest?.("a[href^='#']");
  if (!link) return;
  event.preventDefault();
  const id = decodeURIComponent(link.getAttribute("href").slice(1));
  if (!id) return;
  const frameTarget = frame.contentDocument?.getElementById(id);
  const target = frameTarget ?? document.getElementById(id);
  if (target) scrollToBlogTarget(frame, target, !!frameTarget);
};

export const HtmlFeatureBlogContainer = ({ details, position }) => {
  const [blogHtml, setBlogHtml] = useState(null);
  const [frameHeight, setFrameHeight] = useState(0);
  const frameRef = useRef(null);
  const observerRef = useRef(null);

  const source = getBlogSource(details);
  const sourceType = source?.type;
  const sourceId = source?.id;

  useEffect(() => {
    setBlogHtml(null);
    if (!BLOG_BASE_URL || !sourceType || !sourceId || !position) return;
    const controller = new AbortController();
    fetch(`${BLOG_BASE_URL}/${sourceType}/${encodeURIComponent(sourceId)}_${position}.html?v=${Date.now()}`, {
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.text() : null))
      .then((html) => {
        if (html && html.trim()) setBlogHtml(html);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [sourceType, sourceId, position]);

  const blogDocument = useMemo(() => (blogHtml ? buildBlogDocument(blogHtml) : null), [blogHtml]);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  const handleFrameLoad = () => {
    const frame = frameRef.current;
    const frameDocument = frame?.contentDocument;
    if (!frameDocument) return;
    const updateHeight = () =>
      setFrameHeight(Math.max(frameDocument.documentElement.scrollHeight, frameDocument.body?.scrollHeight ?? 0));
    frameDocument.addEventListener("click", (event) => handleHashLinkClick(frame, event));
    observerRef.current?.disconnect();
    const FrameResizeObserver = frame.contentWindow?.ResizeObserver ?? window.ResizeObserver;
    if (FrameResizeObserver && frameDocument.body) {
      observerRef.current = new FrameResizeObserver(updateHeight);
      observerRef.current.observe(frameDocument.body);
    }
    updateHeight();
  };

  return (
    <Fragment>
      {blogDocument && (
        <section className="section-padding section-proDescription" id={`section-${position}`}>
          <div className="container-fluid container-lg">
            <div className="row">
              <div className="col-lg-12">
                <iframe
                  ref={frameRef}
                  className="html-feature-blog-frame"
                  title={`Product highlights ${position}`}
                  srcDoc={blogDocument}
                  sandbox={IFRAME_SANDBOX}
                  scrolling="no"
                  style={{ height: frameHeight }}
                  onLoad={handleFrameLoad}
                />
              </div>
            </div>
          </div>
        </section>
      )}
    </Fragment>
  );
};
